/**
 * Commitment Registry — Domain Types
 *
 * Commitments are first-class artifacts representing promises made to
 * customers, partners, or regulators. They link to items and surface whether
 * active cycle priorities cover them (covered/partial/orphaned/no-items).
 */

// ── Enumerations ─────────────────────────────────────────────────────────────

export type CommitmentType = 'customer' | 'partner' | 'regulatory' | 'internal';
/**
 * 'suspended' and 'superseded' since 959. 'superseded' is entered only by
 * supersede_commitment after a continuity ruling (983); a status edit never sets or leaves it.
 */
export type CommitmentStatus = 'active' | 'fulfilled' | 'expired' | 'cancelled' | 'suspended' | 'superseded';
/** A person's ruling on whether the commitment keeps its identity (983, D1). */
export type CommitmentContinuityRuling = 'same' | 'different';
export type CommitmentPriority = 'critical' | 'high' | 'medium' | 'low';

/**
 * Coverage status derived from whether linked items participate in an active cycle.
 *   covered  — all linked items are in an active cycle
 *   partial  — some (but not all) linked items are in an active cycle
 *   at-risk  — linked items exist but none are in any active cycle
 *   no-items — no items linked to this commitment yet
 */
export type CommitmentCoverage = 'covered' | 'partial' | 'at-risk' | 'no-items';

// ── Core domain objects ───────────────────────────────────────────────────────

export interface Commitment {
  id: string;
  workspaceId: string;
  title: string;
  description: string | null;
  commitmentType: CommitmentType;
  status: CommitmentStatus;
  priority: CommitmentPriority;
  /** ISO date string (YYYY-MM-DD), no time component */
  dueDate: string | null;
  ownerId: string | null;
  ownerName: string | null;
  /** Name of the customer, partner, or regulatory body */
  counterparty: string | null;
  createdBy: string | null;
  createdAt: Date;
  updatedAt: Date;
  /** The standing continuity ruling (983); consumed when the commitment is superseded. */
  continuityRuling: CommitmentContinuityRuling | null;
  continuityBasis: string | null;
  continuityRuledBy: string | null;
  continuityRuledAt: string | null;
  /** On a successor: the commitment it replaced. */
  supersedesCommitmentId: string | null;
  /** On a superseded commitment: its one successor (NULL after a split; successors name it instead). */
  supersededByCommitmentId: string | null;
  supersededAt: string | null;
}

export interface CommitmentItemLink {
  id: string;
  commitmentId: string;
  itemId: string;
  workspaceId: string;
  linkedAt: Date;
  linkedBy: string | null;
}

/** Raw row returned by the get_commitment_coverage RPC */
export interface CommitmentCoverageSummary {
  commitmentId: string;
  linkedItemCount: number;
  coveredItemCount: number;
  /** Item IDs that are in a non-terminal cycle (the "covered" subset) */
  coveredItemIds: string[];
  coverageStatus: CommitmentCoverage;
}

/** Commitment with resolved coverage data — used as the primary display object */
export interface CommitmentWithCoverage extends Commitment {
  linkedItemIds: string[];
  /** Subset of linkedItemIds that are in a non-terminal cycle */
  coveredItemIds: string[];
  coverage: CommitmentCoverage;
  coveredItemCount: number;
  linkedItemCount: number;
}

// ── Input shapes ──────────────────────────────────────────────────────────────

export interface CreateCommitmentInput {
  title: string;
  description?: string;
  commitmentType: CommitmentType;
  status?: CommitmentStatus;
  priority?: CommitmentPriority;
  dueDate?: string | null;
  ownerId?: string | null;
  ownerName?: string | null;
  counterparty?: string | null;
}

export type UpdateCommitmentInput = Partial<CreateCommitmentInput> & {
  status?: CommitmentStatus;
};

// ── DB row shapes (snake_case from Supabase) ──────────────────────────────────

export interface CommitmentRow {
  id: string;
  workspace_id: string;
  title: string;
  description: string | null;
  commitment_type: string;
  status: string;
  priority: string;
  due_date: string | null;
  owner_id: string | null;
  owner_name: string | null;
  counterparty: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  continuity_ruling?: string | null;
  continuity_basis?: string | null;
  continuity_ruled_by?: string | null;
  continuity_ruled_at?: string | null;
  supersedes_commitment_id?: string | null;
  superseded_by_commitment_id?: string | null;
  superseded_at?: string | null;
}

export interface CommitmentItemLinkRow {
  id: string;
  commitment_id: string;
  item_id: string;
  workspace_id: string;
  linked_at: string;
  linked_by: string | null;
}

export interface CommitmentCoverageRow {
  commitment_id: string;
  linked_item_count: number;
  covered_item_count: number;
  covered_item_ids: string[];
  coverage_status: string;
}

// ── Row → domain mappers ──────────────────────────────────────────────────────

export function commitmentRowToDomain(row: CommitmentRow): Commitment {
  return {
    id: row.id,
    workspaceId: row.workspace_id,
    title: row.title,
    description: row.description,
    commitmentType: row.commitment_type as CommitmentType,
    status: row.status as CommitmentStatus,
    priority: row.priority as CommitmentPriority,
    dueDate: row.due_date,
    ownerId: row.owner_id,
    ownerName: row.owner_name,
    counterparty: row.counterparty,
    createdBy: row.created_by,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
    continuityRuling: (row.continuity_ruling as CommitmentContinuityRuling | null | undefined) ?? null,
    continuityBasis: row.continuity_basis ?? null,
    continuityRuledBy: row.continuity_ruled_by ?? null,
    continuityRuledAt: row.continuity_ruled_at ?? null,
    supersedesCommitmentId: row.supersedes_commitment_id ?? null,
    supersededByCommitmentId: row.superseded_by_commitment_id ?? null,
    supersededAt: row.superseded_at ?? null,
  };
}

export function commitmentLinkRowToDomain(row: CommitmentItemLinkRow): CommitmentItemLink {
  return {
    id: row.id,
    commitmentId: row.commitment_id,
    itemId: row.item_id,
    workspaceId: row.workspace_id,
    linkedAt: new Date(row.linked_at),
    linkedBy: row.linked_by,
  };
}

export function commitmentCoverageRowToDomain(row: CommitmentCoverageRow): CommitmentCoverageSummary {
  return {
    commitmentId: row.commitment_id,
    linkedItemCount: row.linked_item_count,
    coveredItemCount: row.covered_item_count,
    coveredItemIds: row.covered_item_ids ?? [],
    coverageStatus: row.coverage_status as CommitmentCoverage,
  };
}
